import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";
import useProductStoryPlayer from "./useProductStoryPlayer";
import "./ProductVideoExperience.css";

const VideoContext = createContext(null);
const clamp = (value) => Math.max(0, Math.min(1, value));

function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [query]);
  return matches;
}

function updateGlow(event) {
  const video = event.currentTarget;
  const progress = video.duration ? video.currentTime / video.duration : 0;
  video.closest(".product-video-light")?.style.setProperty("--video-energy", String(0.12 + Math.sin(clamp(progress) * Math.PI) * 0.28));
}

export function ProductVideoProvider({ children }) {
  const [active, setActive] = useState(null);
  const activeRef = useRef(null);
  const manualOwner = useRef(false);
  const players = useRef(new Map());
  const [visible, setVisible] = useState(!document.hidden);
  const finePointer = useMediaQuery("(hover: hover) and (pointer: fine)");
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const automatic = finePointer && !reducedMotion && !navigator.connection?.saveData;

  const register = useCallback((surface, video) => {
    if (video) players.current.set(surface, video);
    else players.current.delete(surface);
  }, []);
  const release = useCallback((surface) => {
    if (activeRef.current !== surface) return;
    activeRef.current = null;
    manualOwner.current = false;
    setActive(null);
  }, []);
  const claim = useCallback((surface, manual = false) => {
    if (document.hidden) return false;
    if (activeRef.current === surface) {
      if (manual) manualOwner.current = true;
      return true;
    }
    if (manualOwner.current && !manual) return false;
    // Pause the previous owner synchronously before the next play/seek.
    activeRef.current = surface;
    manualOwner.current = manual;
    players.current.forEach((video, key) => { if (key !== surface) video.pause(); });
    setActive(surface);
    return true;
  }, []);
  const owns = useCallback((surface) => activeRef.current === surface && !document.hidden, []);

  useEffect(() => {
    const onVisibility = () => {
      setVisible(!document.hidden);
      if (document.hidden) {
        players.current.forEach((video) => video.pause());
        activeRef.current = null;
        manualOwner.current = false;
        setActive(null);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("carthy-video-performance", { detail: { playing: active !== null } }));
  }, [active]);
  useEffect(() => () => {
    window.dispatchEvent(new CustomEvent("carthy-video-performance", { detail: { playing: false } }));
  }, []);

  return (
    <VideoContext.Provider value={{ active, claim, release, owns, register, visible, automatic, reducedMotion }}>
      {children}
    </VideoContext.Provider>
  );
}

export function ProductVideoPreview({ product, alt, priority = false }) {
  const surface = `preview:${product.id}`;
  const { active, claim, release, owns, register, automatic, visible } = useContext(VideoContext);
  const container = useRef(null);
  const videoRef = useRef(null);
  const [requested, setRequested] = useState(false);
  const [ready, setReady] = useState(false);
  const playing = active === surface && automatic && visible;

  useEffect(() => {
    const video = videoRef.current;
    register(surface, video);
    return () => { video.pause(); register(surface, null); release(surface); };
  }, [register, release, surface]);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) release(surface);
    }, { threshold: 0 });
    observer.observe(container.current);
    return () => observer.disconnect();
  }, [release, surface]);
  useEffect(() => {
    const video = videoRef.current;
    let cancelled = false;
    if (playing && requested) {
      video.play().then(() => {
        if (!owns(surface)) video.pause();
      }).catch(() => { if (!cancelled) release(surface); });
    } else {
      video.pause();
      setReady(false);
      if (video.readyState > 0) video.currentTime = 0;
      release(surface);
    }
    return () => { cancelled = true; video.pause(); };
  }, [playing, requested, owns, release, surface]);

  return (
    <div ref={container} className="product-video-preview product-video-light" data-product-id={product.id}
      onPointerEnter={(event) => {
        if (event.pointerType === "mouse" && automatic && claim(surface)) setRequested(true);
      }}
      onPointerLeave={() => release(surface)}>
      <img src={product.poster} alt={alt} loading={priority ? "eager" : "lazy"} fetchPriority={priority ? "high" : undefined} decoding="async" className="product-video-poster" />
      <video ref={videoRef} src={requested ? product.src : undefined} muted playsInline preload="none"
        aria-hidden="true" tabIndex={-1} className="product-video-inline" data-product-video="preview" data-product-id={product.id}
        data-playing={playing && ready} onPlaying={() => setReady(true)} onTimeUpdate={updateGlow}
        onEnded={() => release(surface)} onError={() => { setReady(false); release(surface); }} />
      <a className="product-video-trigger" href={`#${product.storyId}`} onClick={() => release(surface)}>
        <Play size={16} aria-hidden="true" /><span>Explorer l’intérieur</span>
      </a>
    </div>
  );
}

export function ProductVideoStory({ product }) {
  const controller = useContext(VideoContext);
  const largeScreen = useMediaQuery("(min-width: 1024px) and (min-height: 720px)");
  const enhanced = controller.automatic && largeScreen;
  const player = useProductStoryPlayer({ product, controller, enhanced });
  const { mode, progress, phaseIndex, ready, failed, loadVideo, trackRef, stageRef, videoRef } = player;
  const headingId = `${product.id}-story-title`;
  const videoId = `${product.id}-story-video`;
  const phase = product.phases[phaseIndex];
  const moving = mode === "playing" || mode === "transition";

  return (
    <section id={product.storyId} className="products-section product-story" aria-labelledby={headingId} data-product-id={product.id}>
      <div className="product-story-heading">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#76b900]">{product.eyebrow}</p>
        <h2 id={headingId} className="mt-4 font-display text-3xl font-semibold tracking-tighter md:text-5xl">{product.storyTitle}</h2>
        <p className="mt-5 max-w-2xl text-white/60" key={enhanced && mode === "scroll" ? "scroll" : "manual"}>
          {enhanced && mode === "scroll" ? product.scrollHint : "Choisissez une étape ou lancez la vidéo."}
        </p>
      </div>
      <div ref={trackRef} className="product-story-track" data-enhanced={enhanced && !failed} data-mode={mode}>
        <div ref={stageRef} className="product-story-stage product-video-light" style={{ "--video-energy": 0.12 + Math.sin(progress * Math.PI) * 0.28 }}>
          <div className="product-story-layout">
            <div className="product-story-visual">
              {!ready && <img src={product.poster} alt={product.name} loading="lazy" decoding="async" />}
              <video ref={videoRef} id={videoId} src={loadVideo ? product.src : undefined} muted playsInline preload={loadVideo ? "auto" : "none"}
                aria-label={product.videoTitle} tabIndex={-1} data-product-video="story" data-product-id={product.id}
                style={{ opacity: ready ? 1 : 0 }} onLoadedData={player.onLoadedData} onLoadedMetadata={player.onMetadata}
                onTimeUpdate={player.onTimeUpdate} onEnded={player.stop} onError={player.onError} />
            </div>
            <div className="product-story-copy">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#76b900]">{product.name}</p>
              <div key={phaseIndex}><h3>{phase.title}</h3><p>{phase.text}</p></div>
              <div className="product-story-controls">
                <button type="button" className="product-story-open" aria-controls={videoId} onClick={player.togglePlay}>
                  {moving ? <Pause size={16} aria-hidden="true" /> : <Play size={16} aria-hidden="true" />}
                  <span key={moving ? "pause" : "play"}>{moving ? "Pause" : "Lire la vidéo"}</span>
                </button>
                {enhanced && !failed && mode !== "scroll" && <button type="button" className="product-story-scroll" onClick={player.resumeScroll}>Reprendre au défilement</button>}
              </div>
              <p className="text-xs text-white/40">Animation de présentation.</p>
              {failed && <div className="product-video-error" role="status"><p>La vidéo n’a pas pu être chargée.</p>
                <button type="button" onClick={player.retry}>Réessayer</button>
              </div>}
            </div>
          </div>
          <ol className="product-story-steps">{product.phases.map((item, index) =>
            <li key={item.label} data-active={phaseIndex === index}>
              <button type="button" className="product-story-step" aria-controls={videoId} aria-current={phaseIndex === index ? "step" : undefined} onClick={() => player.goToStep(index)}>
                <span className="product-step-number" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span><span>{item.label}</span>
              </button>
            </li>
          )}</ol>
          <div className="product-story-progress" aria-hidden="true"><span style={{ width: `${progress * 100}%` }} /></div>
        </div>
      </div>
    </section>
  );
}
